import React, { useState } from "react";
import { MessageSquareWarning, Bug } from "lucide-react";
import { Modal } from "./Modal";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";

const CATEGORIES = ["bug", "payment", "access", "visual", "suggestion", "other"];
const field = "w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/60 px-3 py-2.5 text-sm text-xmas-snow placeholder:text-xmas-slate/70 focus:border-xmas-gold focus:outline-none";

export function FeedbackModal({ open, onClose, origin = "landing" }) {
  const { t, lang } = useI18n();
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("other");
  const [page, setPage] = useState(() => window.location.pathname);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");

  const reset = () => { setDesc(""); setCat("other"); setPage(window.location.pathname); setState("idle"); setErr(""); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (desc.trim().length < 10) { setErr(t("fb.min")); return; }
    setState("sending"); setErr("");
    try {
      await api.post("/feedback", {
        description: desc.trim(), category: cat, page: page.slice(0, 300), origin, lang,
        market: localStorage.getItem("mc_market") || null,
        tech: { device: window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop", viewport: `${window.innerWidth}x${window.innerHeight}`, screen: `${window.screen.width}x${window.screen.height}`, user_agent: navigator.userAgent.slice(0, 300), platform: navigator.platform, tz: Intl.DateTimeFormat().resolvedOptions().timeZone, referrer: document.referrer.slice(0, 300) },
      });
      setState("done");
    } catch (ex) {
      setState("idle");
      setErr(ex?.response?.status === 429 ? t("fb.too_many") : t("fb.error"));
    }
  };

  return (
    <Modal open={open} onClose={close} title={t("fb.title")} testid="feedback-modal">
      {state === "done" ? (
        <div className="py-6 text-center" data-testid="feedback-success">
          <p className="text-3xl">🎄</p>
          <p className="mt-3 text-base text-xmas-snow">{t("fb.thanks")}</p>
          <button onClick={close} data-testid="feedback-close-btn" className="btn-gold mt-6 rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-wider">{t("fb.close")}</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
          <p className="text-sm text-xmas-champagne">{t("fb.intro")}</p>
          <label className="block text-xs uppercase tracking-wider text-xmas-slate">{t("fb.desc")} *
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={3000} rows={5} required placeholder={t("fb.desc_ph")} className={`${field} mt-1.5 resize-y`} data-testid="feedback-description" />
            <span className="mt-1 block text-right text-[11px] normal-case tracking-normal text-xmas-slate/70">{desc.length}/3000</span>
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-xs uppercase tracking-wider text-xmas-slate">{t("fb.category")}
              <select value={cat} onChange={(e) => setCat(e.target.value)} className={`${field} mt-1.5`} data-testid="feedback-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`fb.cat.${c}`)}</option>)}
              </select>
            </label>
            <label className="block text-xs uppercase tracking-wider text-xmas-slate">{t("fb.page")}
              <input value={page} onChange={(e) => setPage(e.target.value)} maxLength={300} className={`${field} mt-1.5`} data-testid="feedback-page" />
            </label>
          </div>
          {err && <p className="text-sm text-red-300" data-testid="feedback-error">{err}</p>}
          <button type="submit" disabled={state === "sending"} data-testid="feedback-submit" className="btn-gold w-full rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60 sm:w-auto">{state === "sending" ? t("fb.sending") : t("fb.submit")}</button>
        </form>
      )}
    </Modal>
  );
}

export function FeedbackLink({ origin = "landing", className = "" }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="feedback-open-link" className={`inline-flex items-center gap-1.5 underline-offset-4 transition hover:text-xmas-gold hover:underline ${className}`}>
        <MessageSquareWarning className="h-3.5 w-3.5" /> {t("fb.link")}
      </button>
      <FeedbackModal open={open} onClose={() => setOpen(false)} origin={origin} />
    </>
  );
}

export function FeedbackButton({ origin = "dashboard", className = "" }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="feedback-open-button" className={`glass inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider text-xmas-champagne transition hover:border-xmas-gold hover:text-xmas-gold ${className}`}>
        <Bug className="h-4 w-4 text-xmas-gold" /> {t("fb.button")}
      </button>
      <FeedbackModal open={open} onClose={() => setOpen(false)} origin={origin} />
    </>
  );
}
